import { yupResolver } from '@hookform/resolvers/yup';
import { Button, CircularProgress, makeStyles, Typography } from '@material-ui/core';
import React, { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { ObjectShape } from 'yup/lib/object';
import DatePicker from 'components/CustomInputs/DatePicker';
import CollapsableCard from 'components/Generic/CollapsableCard';
import { notify } from 'components/Notifier';
import { AppContext } from 'context';
import { ReportDownloadStatus } from 'enums/reports';
import { DefaultExpeditionClientFormValues, ExpeditionClientReportForm } from '../types';

const useStyles = makeStyles(() => ({
  infoDisplay: {
    width: '80%',
  },
  collapsableContent: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: '0 20px',
    justifyContent: 'space-between',
  },
  inputFieldsWrapper: {
    width: '55%',
  },
  clientInputField: {
    width: '45%',
  },
  startDateInputField: {
    width: '25%',
    margin: '0 20px',

    '& input': {
      cursor: 'pointer',
    },
  },
  endDateInputField: {
    width: '25%',

    '& input': {
      cursor: 'pointer',
    },
  },
  downloadButton: {
    marginRight: '10px',
  },
}));

type DownloadReportCardProps = {
  collapseTitle: string;
  formId: string;
  yupSchemaShape: ObjectShape;
  defaultFormValue: DefaultExpeditionClientFormValues;
  handleClickDownload: (sortingCenterId: string, form: ExpeditionClientReportForm) => Promise<void>;
};

const DownloadExpeditionReportCard: React.FC<DownloadReportCardProps> = ({
  collapseTitle,
  formId,
  handleClickDownload,
  yupSchemaShape,
  defaultFormValue,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const context = useContext(AppContext);
  const sortingCenterId = context.getSortingCenter?.()?.['@id'] ?? null;
  const sortingCenter = context.getSortingCenter?.();
  const [downloadStatus, setDownloadStatus] = useState<ReportDownloadStatus>(ReportDownloadStatus.INITIAL);

  const onSubmit = async ({ startDate, endDate }: DefaultExpeditionClientFormValues) => {
    if (!sortingCenterId) return;
    if (!startDate || !endDate) return;

    setDownloadStatus(ReportDownloadStatus.PENDING);

    handleClickDownload(sortingCenterId, { startDate, endDate })
      .then(() => {
        setDownloadStatus(ReportDownloadStatus.SUCCESS);
        notify({
          message: t('reports.download.success.long'),
          severity: 'success',
        });
      })
      .catch(e => {
        setDownloadStatus(ReportDownloadStatus.ERROR);
        if (452 === e.status) {
          notify({
            message: t('reports.client.error.noExpeditionsForDates'),
            severity: 'error',
          });
        } else {
          notify({
            message: t('reports.download.error'),
            severity: 'error',
          });
        }
      });
  };

  const schema = yup.object().shape(yupSchemaShape);

  const { control, handleSubmit, reset } = useForm({
    defaultValues: defaultFormValue,
    resolver: yupResolver(schema, { abortEarly: false }),
  });

  const minStartDate = sortingCenter?.initializationDate ? new Date(sortingCenter.initializationDate) : new Date();
  const maxEndDate = new Date();

  const [minEndDate, setMinEndDate] = useState<Date | null>(minStartDate);
  const [maxStartDate, setMaxStartDate] = useState<Date | null>(maxEndDate);

  useEffect(() => reset(defaultFormValue), [reset, defaultFormValue]);

  return (
    <CollapsableCard
      maxWidth="942px"
      collapsableContent={
        <form id={formId} className={classes.collapsableContent} onSubmit={handleSubmit(onSubmit)}>
          <div>
            <Controller
              name="startDate"
              control={control}
              render={({ field: { ref, onChange, ...inputProps }, fieldState }) => (
                <DatePicker
                  id="startDate"
                  variant="inline"
                  inputVariant="standard"
                  disableFuture
                  disableToolbar
                  autoOk
                  maxDate={maxStartDate}
                  minDate={minStartDate}
                  format="dd/MM/yyyy"
                  disabled={downloadStatus === ReportDownloadStatus.PENDING}
                  inputRef={ref}
                  className={classes.startDateInputField}
                  placeholder={t('reports.client.expeditionClient.startDate')}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  onChange={e => {
                    onChange(e);
                    if (!e || (!!e && e < minStartDate)) {
                      setMinEndDate(minStartDate);
                      return;
                    }
                    setMinEndDate(e);
                  }}
                  {...inputProps}
                />
              )}
            />
            <Controller
              name="endDate"
              control={control}
              render={({ field: { ref, onChange, ...inputProps }, fieldState }) => (
                <DatePicker
                  id="endDate"
                  variant="inline"
                  inputVariant="standard"
                  disableFuture
                  disableToolbar
                  format="dd/MM/yyyy"
                  autoOk
                  maxDate={maxEndDate}
                  minDate={minEndDate}
                  disabled={downloadStatus === ReportDownloadStatus.PENDING}
                  inputRef={ref}
                  className={classes.endDateInputField}
                  placeholder={t('reports.client.expeditionClient.endDate')}
                  error={fieldState.invalid}
                  helperText={fieldState.error?.message}
                  onChange={e => {
                    onChange(e);
                    if (!e || (!!e && e > maxEndDate)) {
                      setMaxStartDate(maxEndDate);
                      return;
                    }
                    setMaxStartDate(e);
                  }}
                  {...inputProps}
                />
              )}
            />
          </div>
          <div>
            {downloadStatus === ReportDownloadStatus.INCOMING_SOON && (
              <Typography color="error">{t('reports.download.incomingSoon')}</Typography>
            )}
            {downloadStatus === ReportDownloadStatus.NOT_PROCESSABLE && (
              <Typography color="error">{t('reports.download.nonClosedMonth')}</Typography>
            )}
            {downloadStatus === ReportDownloadStatus.SUCCESS && (
              <Typography color="primary">{t('reports.download.success.short')}</Typography>
            )}
            {downloadStatus === ReportDownloadStatus.PENDING && <CircularProgress size={25} />}
          </div>
          <div>
            <Button
              size="small"
              variant="outlined"
              className={classes.downloadButton}
              type="submit"
              form={formId}
              disabled={
                downloadStatus === ReportDownloadStatus.INCOMING_SOON ||
                downloadStatus === ReportDownloadStatus.PENDING ||
                downloadStatus === ReportDownloadStatus.NOT_PROCESSABLE
              }
            >
              {t('reports.button.download')}
            </Button>
          </div>
        </form>
      }
    >
      <Typography className={classes.infoDisplay}>{collapseTitle}</Typography>
    </CollapsableCard>
  );
};
export default DownloadExpeditionReportCard;
